import { Controller } from "stimulus"

// Preserve search terms across navigation
export default class extends Controller {
  static targets = ["input", "button", "icon"]
  static values = { 
    page: String,
    clearUrl: String
  }

  connect() {
    this.updateLinksOnPageLoad()
    this.populateInputFromCurrentSearch()
    this.updateSearchButton()
    this.attachSubmitHandler()
  }

  attachSubmitHandler() {
    if (this.hasInputTarget) {
      const form = this.inputTarget.closest('form')
      if (form) {
        this.boundHandleSubmit = this.handleSubmit.bind(this)
        form.addEventListener('submit', this.boundHandleSubmit)
      }
    }
  }

  handleSubmit(event) {
    event.preventDefault()
    
    const input = event.target.querySelector('[data-search-preservation-target~="input"]') || this.inputTarget
    const searchQuery = input.value.trim()
    
    if (!searchQuery) {
      const form = event.target
      window.location.href = form.action
      return
    }
    
    // Always redirect to portal with search query
    window.location.href = `/portal?search_query=${encodeURIComponent(searchQuery)}`
  }

  handleSearchButtonClick(event) {
    const input = this.searchInputFor(event.currentTarget)
    if (!input || !input.value.trim()) return

    event.preventDefault()
    event.stopPropagation()

    this.clearSearch()
    window.location.href = this.hasClearUrlValue ? this.clearUrlValue : window.location.pathname
  }

  disconnect() {
    if (this.hasInputTarget && this.boundHandleSubmit) {
      const form = this.inputTarget.closest('form')
      if (form) {
        form.removeEventListener('submit', this.boundHandleSubmit)
      }
    }
  }

  updateLinksOnPageLoad() {
    // A filter chosen inside The Cut may search every edition, but it is not a
    // navbar search and must never be persisted or propagated to other pages.
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('cut_filter') === '1') {
      sessionStorage.removeItem('currentSearchTerm')
      return
    }

    // Get search term from URL parameters or sessionStorage
    const searchTerm = this.getCurrentSearch()
    
    if (searchTerm) {
      // Update navigation links
      this.updateNavigationLinks(searchTerm)
      
      // Store in sessionStorage for persistence
      sessionStorage.setItem('currentSearchTerm', searchTerm)
    }
  }

  populateInputFromCurrentSearch() {
    if (!this.hasInputTarget) return

    const searchTerm = this.getCurrentSearch()
    if (searchTerm && !this.inputTarget.value) {
      this.inputTarget.value = searchTerm
    }
  }

  clearSearch() {
    sessionStorage.removeItem('currentSearchTerm')

    document.querySelectorAll('[data-search-preservation-target~="input"]').forEach(input => {
      input.value = ''
    })

    document.querySelectorAll('[data-search-preservation-target~="icon"]').forEach(icon => {
      icon.classList.add('fa-magnifying-glass')
      icon.classList.remove('fa-refresh')
    })

    document.querySelectorAll('[data-search-preservation-target~="button"]').forEach(button => {
      button.title = 'Search'
      button.setAttribute('aria-label', 'Search')
    })
  }

  getCurrentSearch() {
    // Try URL parameters first
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('cut_filter') === '1') return null

    let searchTerm = urlParams.get('search') || urlParams.get('search_query') || urlParams.get('search_cut')
    
    // If not in URL, try sessionStorage
    if (!searchTerm) {
      searchTerm = sessionStorage.getItem('currentSearchTerm')
    }
    
    return searchTerm
  }

  updateNavigationLinks(searchTerm) {
    if (!searchTerm) return

    const navLinks = document.querySelectorAll('#nav-links a')
    navLinks.forEach(link => {
      const href = link.getAttribute('href')
      if (href && !href.includes('?')) {
        // Add appropriate search parameter based on the path
        if (href.includes('/portal')) {
          link.setAttribute('href', `${href}?search_query=${encodeURIComponent(searchTerm)}`)
        } else if (href.includes('/editions/')) {
          link.setAttribute('href', `${href}?search_cut=${encodeURIComponent(searchTerm)}`)
        } else if (href.includes('/events') || href.includes('/consultations') || href.includes('/podcasts') || href.includes('/trainings')) {
          link.setAttribute('href', `${href}?search=${encodeURIComponent(searchTerm)}`)
        }
      }
    })
  }

  searchChanged(event) {
    const input = event && event.target ? event.target : this.inputTarget
    const searchTerm = input.value
    if (searchTerm) {
      sessionStorage.setItem('currentSearchTerm', searchTerm)
      this.updateNavigationLinks(searchTerm)
    } else {
      sessionStorage.removeItem('currentSearchTerm')
    }

    this.updateSearchButton(input)
  }

  updateSearchButton(input = null) {
    input = input || (this.hasInputTarget ? this.inputTarget : null)
    const form = input && input.closest('form')
    const icon = form ? form.querySelector('[data-search-preservation-target~="icon"]') : (this.hasIconTarget ? this.iconTarget : null)
    if (!icon) return

    const button = form ? form.querySelector('[data-search-preservation-target~="button"]') : (this.hasButtonTarget ? this.buttonTarget : null)
    const hasSearch = input && input.value.trim().length > 0
    icon.classList.toggle('fa-magnifying-glass', !hasSearch)
    icon.classList.toggle('fa-refresh', hasSearch)

    if (button) {
      button.title = hasSearch ? 'Clear search' : 'Search'
      button.setAttribute('aria-label', hasSearch ? 'Clear search' : 'Search')
    }
  }

  searchInputFor(element) {
    const form = element.closest('form')
    if (form) return form.querySelector('[data-search-preservation-target~="input"]')
    return this.hasInputTarget ? this.inputTarget : null
  }
}
